import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL || '';

const GlobalSearch = () => {
  const { t } = useTranslation('common');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const timeoutRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const doSearch = async (q) => {
    if (q.length < 2) { setResults([]); setOpen(false); return; }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/search/global?q=${encodeURIComponent(q)}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setResults(data.results || []);
        setOpen(true);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => doSearch(val), 300);
  };

  const handleSelect = (route) => {
    setOpen(false);
    setQuery('');
    navigate(route);
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  return (
    <div className="relative" ref={wrapperRef} data-testid="global-search">
      <div className="flex items-center bg-slate-100 rounded-lg px-2 py-1.5 gap-1.5">
        <Search className="h-4 w-4 text-slate-400 flex-shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleChange}
          placeholder={t('search_placeholder', 'Cauta...')}
          className="bg-transparent text-sm outline-none w-full min-w-0 text-slate-700 placeholder:text-slate-400"
          data-testid="global-search-input"
        />
        {query && (
          <button onClick={clearSearch} className="flex-shrink-0">
            <X className="h-3.5 w-3.5 text-slate-400" />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-80 overflow-y-auto z-50" data-testid="search-results">
          {loading ? (
            <div className="p-3 text-sm text-slate-500 text-center">{t('search_loading', 'Se cauta...')}</div>
          ) : results.length === 0 ? (
            <div className="p-3 text-sm text-slate-500 text-center">{t('search_no_results', 'Niciun rezultat')}</div>
          ) : (
            results.map((group, gi) => (
              <div key={gi}>
                <div className="px-3 py-1.5 bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider sticky top-0 border-b border-slate-100">
                  {group.category}
                </div>
                {group.items.map((item, ii) => (
                  <button
                    key={`${gi}-${ii}`}
                    onClick={() => handleSelect(group.route)}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center gap-2 border-b border-slate-50 last:border-0"
                    data-testid={`search-item-${gi}-${ii}`}
                  >
                    <span className="text-sm text-slate-800">{item.label}</span>
                    {item.detail && <span className="text-xs text-slate-400">{item.detail}</span>}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
